import { useState, useEffect, useCallback } from 'react';
import {
  listAllDeeplinks,
  adminUpdateDeeplink,
  adminDeleteDeeplink,
  type DeeplinkWithOwner,
  type AdminUpdateDeeplinkInput,
} from '@/services/admin';

export const useAdminDeeplinks = () => {
  const [deeplinks, setDeeplinks] = useState<DeeplinkWithOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchDeeplinks = useCallback(async (searchTerm?: string) => {
    try {
      setLoading(true);
      setError(null);
      const result = await listAllDeeplinks({ search: searchTerm });
      setDeeplinks(result.items);
      setTotal(result.total);
      setHasMore(result.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch deeplinks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeeplinks(search);
  }, [fetchDeeplinks, search]);

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
  }, []);

  const update = async (id: string, input: AdminUpdateDeeplinkInput) => {
    try {
      const updatedDeeplink = await adminUpdateDeeplink(id, input);
      setDeeplinks((prev) =>
        prev.map((d) => (d.id === id ? { ...d, ...updatedDeeplink } : d))
      );
      return updatedDeeplink;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update deeplink';
      setError(message);
      throw err;
    }
  };

  const remove = async (id: string) => {
    try {
      await adminDeleteDeeplink(id);
      setDeeplinks((prev) => prev.filter((d) => d.id !== id));
      setTotal((prev) => prev - 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete deeplink';
      setError(message);
      throw err;
    }
  };

  return {
    deeplinks,
    loading,
    error,
    search,
    total,
    hasMore,
    handleSearch,
    update,
    remove,
    refresh: () => fetchDeeplinks(search),
  };
};
